import { useState, useEffect } from "react";
import { 
  User, 
  Rocket, 
  TrendingUp, 
  Briefcase, 
  PenTool, 
  BookOpen,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

export type ImpersonatedRole = 
  | "explorer" 
  | "founder" 
  | "investor" 
  | "advisor" 
  | "contributor" 
  | "executive" 
  | null;

interface RoleSwitcherProps {
  currentRole: ImpersonatedRole;
  onRoleChange: (role: ImpersonatedRole) => void;
}

const roleConfig = [
  { id: "explorer" as const, label: "Explorer", icon: BookOpen, description: "Free access to content" },
  { id: "founder" as const, label: "Founder", icon: Rocket, description: "Startup founder view" },
  { id: "investor" as const, label: "Investor", icon: TrendingUp, description: "Investor dealroom access" },
  { id: "advisor" as const, label: "Advisor", icon: User, description: "Advisory features" },
  { id: "contributor" as const, label: "Contributor", icon: PenTool, description: "Content publishing" },
  { id: "executive" as const, label: "Executive", icon: Briefcase, description: "Premium networking" },
];

export function RoleSwitcher({ currentRole, onRoleChange }: RoleSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  const currentConfig = currentRole 
    ? roleConfig.find(r => r.id === currentRole) 
    : null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2"
          data-testid="button-role-switcher"
        >
          {currentConfig ? (
            <>
              <currentConfig.icon className="h-4 w-4" />
              <span className="hidden sm:inline" data-testid="text-role-view-as">View as: {currentConfig.label}</span>
              <span className="sm:hidden" data-testid="text-role-mobile">{currentConfig.label}</span>
            </>
          ) : (
            <>
              <User className="h-4 w-4" />
              <span className="hidden sm:inline" data-testid="text-role-view-as">View as Role</span>
              <span className="sm:hidden" data-testid="text-role-mobile">Role</span>
            </>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>Impersonate Role</span>
          {currentRole && (
            <Badge variant="secondary" className="text-xs">Active</Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {roleConfig.map((role) => (
          <DropdownMenuItem 
            key={role.id}
            onClick={() => {
              onRoleChange(role.id);
              setIsOpen(false);
            }}
            className="flex items-start gap-3 cursor-pointer"
            data-testid={`menu-item-role-${role.id}`}
          >
            <role.icon className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex flex-col">
              <span className="font-medium">{role.label}</span>
              <span className="text-xs text-muted-foreground">{role.description}</span>
            </div>
            {currentRole === role.id && (
              <Badge variant="default" className="ml-auto text-xs">Current</Badge>
            )}
          </DropdownMenuItem>
        ))}
        {currentRole && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => {
                onRoleChange(null);
                setIsOpen(false);
              }}
              className="text-destructive cursor-pointer"
              data-testid="menu-item-exit-impersonation"
            >
              Exit Impersonation
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function useRoleImpersonation() {
  const STORAGE_KEY = "chief-in-tech-impersonated-role";
  const validRoles = ["explorer", "founder", "investor", "advisor", "contributor", "executive"];
  
  const [impersonatedRole, setImpersonatedRole] = useState<ImpersonatedRole>(null);
  
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored && validRoles.includes(stored)) {
        setImpersonatedRole(stored as ImpersonatedRole);
      }
    } catch {
    }
  }, []);
  
  const setRole = (role: ImpersonatedRole) => {
    setImpersonatedRole(role);
    try {
      if (role) {
        sessionStorage.setItem(STORAGE_KEY, role);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
    }
  };
  
  return {
    impersonatedRole,
    setImpersonatedRole: setRole,
    isImpersonating: impersonatedRole !== null,
  };
}
